import React from "react";
import {App as AntdApp, Badge, Button, Checkbox, Collapse, Empty, Input, Skeleton, Space, Switch, Tag, Tooltip, Typography} from "antd";
import {FileTextOutlined} from "@ant-design/icons";

import {getCooperativaAiDescriptions, updateAllowedProductFields, updateCooperativaAiEnabled} from "../api/orgApi.js";
import {normalizeFieldKey} from "@modules/fields/utils/fieldRegistry.js";
import AiDescriptionModal from "./AiDescriptionModal.jsx";

const {Text} = Typography;

const TYPE_LABELS = {
	TEXTAREA: "Testo lungo",
	TEXT: "Testo",
	NUMBER: "Numero",
	BOOLEAN: "Booleano",
	DATE: "Data",
	SELECT: "Selezione",
	MULTISELECT: "Selezione multipla"
};

const TYPE_DISPLAY_PRIORITY = ["TEXTAREA", "TEXT", "SELECT", "MULTISELECT", "DATE", "NUMBER", "BOOLEAN"];

function getTypeLabel(type)
{
	if (!type)
		return "Campo";
	const key = String(type).toUpperCase();
	return TYPE_LABELS[key] || key;
}

function normalizeName(name)
{
	return typeof name === "string" ? name.trim().toLowerCase() : "";
}

function getFieldKey(field)
{
	return normalizeFieldKey(field?.key || field?.name);
}

export default function OrgProductFieldsEditor({
	cooperativaId,
	cooperativaName = "",
	availableFields = [],
	allowedFields = [],
	loadingOptions = false,
	onLocalUpdate = () =>
	{
	}
})
{
	const {message, notification} = AntdApp.useApp();
	const [selectedNames, setSelectedNames] = React.useState(() => allowedFields.map(field => field?.key || field?.name || ""));
	const [saving, setSaving] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [aiModalOpen, setAiModalOpen] = React.useState(false);
	const [aiModalFieldKey, setAiModalFieldKey] = React.useState(null);
	const [aiModalFieldLabel, setAiModalFieldLabel] = React.useState(null);
	const [customAiFields, setCustomAiFields] = React.useState(new Set());
	const [aiEnabledKeys, setAiEnabledKeys] = React.useState(new Set());
	const [aiDisabledKeys, setAiDisabledKeys] = React.useState(new Set());

	React.useEffect(() =>
	{
		setSelectedNames(allowedFields.map(field => field?.key || field?.name || ""));
	}, [allowedFields]);

	React.useEffect(() =>
	{
		let active = true;
		if (!cooperativaId)
		{
			setCustomAiFields(new Set());
			setAiEnabledKeys(new Set());
			setAiDisabledKeys(new Set());
			return () => {};
		}

		(async () =>
		{
			try
			{
				const result = await getCooperativaAiDescriptions(cooperativaId);
				if (!active)
					return;

				const next = new Set();
				const aiEnabled = new Set();
				const aiDisabled = new Set();
				const items = Array.isArray(result?.descriptions) ? result.descriptions : [];
				for (const item of items)
				{
					const key = normalizeFieldKey(item.fieldName);
					if (!key)
						continue;

					// Ogni campo restituito è globalmente AI-abilitato (la query backend filtra aiEnabled:true)
					aiEnabled.add(key);
					if (item.aiDisabled)
						aiDisabled.add(key);
					if (item.isCustom)
						next.add(key);
				}
				setCustomAiFields(next);
				setAiEnabledKeys(aiEnabled);
				setAiDisabledKeys(aiDisabled);
			}
			catch
			{
				if (active)
				{
					setCustomAiFields(new Set());
					setAiEnabledKeys(new Set());
					setAiDisabledKeys(new Set());
				}
			}
		})();

		return () =>
		{
			active = false;
		};
	}, [cooperativaId]);

	const availableMap = React.useMemo(() =>
	{
		const map = new Map();
		for (const field of availableFields)
		{
			const key = getFieldKey(field);
			if (!key)
				continue;

			map.set(key, field);
		}
		return map;
	}, [availableFields]);

	const originalMap = React.useMemo(() =>
	{
		const map = new Map();
		for (const field of allowedFields)
		{
			const key = getFieldKey(field);
			if (!key)
				continue;

			map.set(key, field);
		}
		return map;
	}, [allowedFields]);

	const extraSelected = React.useMemo(() =>
	{
		const extras = [];
		for (const field of allowedFields)
		{
			const key = getFieldKey(field);
			if (!key)
				continue;

			if (!availableMap.has(key))
				extras.push(field);
		}
		return extras;
	}, [allowedFields, availableMap]);

	const displayFields = React.useMemo(() =>
	{
		return [...availableFields, ...extraSelected];
	}, [availableFields, extraSelected]);

	const selectedKeySet = React.useMemo(() =>
	{
		const set = new Set();
		for (const name of selectedNames)
		{
			const key = normalizeFieldKey(name);
			if (key)
				set.add(key);
		}
		return set;
	}, [selectedNames]);

	const fieldDescriptors = React.useMemo(() =>
	{
		const descriptors = [];
		const seen = new Set();

		for (const field of displayFields)
		{
			const originalName = field?.name || field?.label || "";
			const key = getFieldKey(field);
			if (!key || seen.has(key))
				continue;

			seen.add(key);

			const typeKey = field?.type ? String(field.type).toUpperCase() : "";
			const aiKeyNormalized = normalizeName(key);
			const typeLabel = getTypeLabel(field?.type);
			const checked = selectedKeySet.has(key);
			const isExtra = !availableMap.has(key);

			descriptors.push({
				key,
				name: originalName || key,
				typeKey: typeKey || "__UNKNOWN__",
				typeLabel,
				checked,
				isExtra,
				aiKey: key,
				aiKeyNormalized: aiKeyNormalized || key
			});
		}

		return descriptors;
	}, [availableMap, displayFields, selectedKeySet]);

	const normalizedSearch = React.useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);

	const filteredDescriptors = React.useMemo(() =>
	{
		if (!normalizedSearch)
			return fieldDescriptors;

		return fieldDescriptors.filter(field => field.name?.toLowerCase().includes(normalizedSearch));
	}, [fieldDescriptors, normalizedSearch]);

	const groupedDescriptors = React.useMemo(() =>
	{
		const groups = new Map();

		for (const field of filteredDescriptors)
		{
			const groupKey = field.typeKey;
			if (!groups.has(groupKey))
			{
				const priorityIndex = TYPE_DISPLAY_PRIORITY.indexOf(groupKey);
				groups.set(groupKey, {
					key: groupKey,
					label: field.typeLabel || "Altro",
					order: priorityIndex === -1 ? TYPE_DISPLAY_PRIORITY.length + (groupKey === "__UNKNOWN__" ? 1 : 0) : priorityIndex,
					fields: []
				});
			}

			groups.get(groupKey).fields.push(field);
		}

		return Array.from(groups.values()).map(group =>
		{
			const selectedCount = group.fields.filter(field => field.checked).length;
			const extraCount = group.fields.filter(field => field.isExtra).length;
			return {
				...group,
				selectedCount,
				extraCount
			};
		}).sort((a, b) =>
		{
			if (a.order !== b.order)
				return a.order - b.order;
			return a.label.localeCompare(b.label, "it", {sensitivity: "base"});
		});
	}, [filteredDescriptors]);

	const [openGroups, setOpenGroups] = React.useState([]);
	const groupsTouchedRef = React.useRef(false);
	const knownGroupKeysRef = React.useRef(new Set());

	React.useEffect(() =>
	{
		const availableKeys = groupedDescriptors.map(group => group.key);
		const known = knownGroupKeysRef.current;
		const newKeys = [];

		for (const key of availableKeys)
		{
			if (!known.has(key))
				newKeys.push(key);
			known.add(key);
		}

		setOpenGroups(prev =>
		{
			const preserved = prev.filter(key => availableKeys.includes(key));

			if (!groupsTouchedRef.current && !prev.length)
				return availableKeys;

			return [...preserved, ...newKeys];
		});
	}, [groupedDescriptors]);

	const initialSelection = React.useMemo(() =>
	{
		return allowedFields.map(field => getFieldKey(field)).filter(Boolean).sort();
	}, [allowedFields]);

	const currentSelection = React.useMemo(() =>
	{
		return Array.from(selectedKeySet).sort();
	}, [selectedKeySet]);

	const hasChanges = React.useMemo(() =>
	{
		if (currentSelection.length !== initialSelection.length)
			return true;

		for (let i = 0; i < currentSelection.length; i++)
		{
			if (currentSelection[i] !== initialSelection[i])
				return true;
		}
		return false;
	}, [currentSelection, initialSelection]);

	const toggleSelection = React.useCallback((name, checked) =>
	{
		const key = normalizeFieldKey(name);
		if (!key)
			return;

		setSelectedNames(prev =>
		{
			const next = new Map();
			for (const value of prev)
			{
				const k = normalizeFieldKey(value);
				if (!k)
					continue;

				if (checked || k !== key)
					next.set(k, value);
			}

			if (checked && !next.has(key))
				next.set(key, key);

			return Array.from(next.values());
		});
	}, [availableMap, originalMap]);

	const resetSelection = React.useCallback(() =>
	{
		setSelectedNames(allowedFields.map(field => getFieldKey(field)));
	}, [allowedFields]);

	const handleGroupToggle = React.useCallback((fields, checked) =>
	{
		setSelectedNames(prev =>
		{
			const map = new Map();

			for (const value of prev)
			{
				const key = normalizeFieldKey(value);
				if (key)
					map.set(key, value);
			}

			if (!checked)
			{
				for (const field of fields)
					map.delete(field.key);
			}
			else
			{
				for (const field of fields)
				{
					if (!map.has(field.key))
						map.set(field.key, field.key);
				}
			}

			return Array.from(map.values());
		});
	}, [availableMap, originalMap]);

	const handleSave = React.useCallback(async () =>
	{
		if (!cooperativaId)
		{
			message.warning("Salva prima la cooperativa per configurare gli input prodotto.");
			return;
		}

		setSaving(true);
		try
		{
			const selection = new Set(Array.from(selectedKeySet));
			const payload = [];

			for (const field of availableFields)
			{
				const key = getFieldKey(field);
				if (!key || !selection.has(key))
					continue;

				payload.push({key, type: field.type});
			}

			for (const field of allowedFields)
			{
				const key = getFieldKey(field);
				if (!key || availableMap.has(key) || !selection.has(key))
					continue;

				payload.push({key, type: field.type});
			}

			const response = await updateAllowedProductFields(cooperativaId, payload);
			if (!response?.ok)
			{
				notification.error({
					message: "Salvataggio non riuscito",
					description: response?.error || "Controlla la risposta dell'API",
					placement: "bottomRight"
				});
				return;
			}

			onLocalUpdate(payload.map((field) => ({name: field.key, type: field.type})));
			message.success("Campi prodotto aggiornati per la cooperativa.");
		}
		finally
		{
			setSaving(false);
		}
	}, [availableFields, allowedFields, availableMap, cooperativaId, message, notification, onLocalUpdate, selectedKeySet]);

	const handleGroupsChange = React.useCallback((keys) =>
	{
		groupsTouchedRef.current = true;

		if (Array.isArray(keys))
			setOpenGroups(keys);
		else if (typeof keys === "string")
			setOpenGroups([keys]);
		else
			setOpenGroups([]);
	}, []);

	const handleOpenAiModal = React.useCallback((field, event) =>
	{
		event?.stopPropagation?.();
		setAiModalFieldKey(field.aiKey || field.key);
		setAiModalFieldLabel(field.name);
		setAiModalOpen(true);
	}, []);

	const handleCloseAiModal = React.useCallback(() =>
	{
		setAiModalOpen(false);
		setAiModalFieldKey(null);
		setAiModalFieldLabel(null);
	}, []);

	const handleAiDescriptionSave = React.useCallback((fieldKey, isCustom) =>
	{
		setCustomAiFields(prev =>
		{
			const next = new Set(prev);
			const normalized = normalizeFieldKey(fieldKey);
			if (!normalized)
				return next;
			if (isCustom)
				next.add(normalized);
			else
				next.delete(normalized);
			return next;
		});
	}, []);

	const handleToggleAiEnabled = React.useCallback(async (field, enabled) =>
	{
		const key = field.aiKeyNormalized;
		if (!key || !cooperativaId)
			return;

		// Aggiornamento ottimistico
		setAiDisabledKeys(prev =>
		{
			const next = new Set(prev);
			if (enabled)
				next.delete(key);
			else
				next.add(key);
			return next;
		});

		const result = await updateCooperativaAiEnabled(cooperativaId, field.aiKey, enabled);
		if (!result?.ok)
		{
			// Rollback in caso di errore
			setAiDisabledKeys(prev =>
			{
				const next = new Set(prev);
				if (enabled)
					next.add(key);
				else
					next.delete(key);
				return next;
			});
			message.error(result?.error || "Errore durante l'aggiornamento dell'AI");
			return;
		}

		message.success(enabled ? "AI attivata per il campo" : "AI disattivata per il campo");
	}, [cooperativaId, message]);

	if (!cooperativaId)
	{
		return (
			<Text type="secondary">
				{cooperativaName ? `Salva la cooperativa “${cooperativaName}” per modificare gli input consentiti.` : "Salva la cooperativa per modificare gli input consentiti."}
			</Text>
		);
	}

	if (loadingOptions)
	{
		return <Skeleton active paragraph={{rows: 4}} title={false}/>;
	}

	if (!displayFields.length)
	{
		return <Empty description="Nessun input disponibile" image={Empty.PRESENTED_IMAGE_SIMPLE}/>;
	}

	const totalSelected = selectedKeySet.size;
	const totalFields = fieldDescriptors.length;
	const hasSearch = Boolean(normalizedSearch);

	return (
		<div className="org-product-fields-editor">
			<div className="org-product-fields-toolbar">
				<Space size={8} wrap className="org-product-fields-summary">
					<Text strong>{`Selezionati ${totalSelected}/${totalFields}`}</Text>
					{extraSelected.length ? (
						<Tag color="gold" bordered={false}>{`${extraSelected.length} fuori catalogo`}</Tag>
					) : null}
				</Space>
				<Input.Search
					allowClear
					className="org-product-fields-search"
					placeholder="Cerca per nome..."
					value={searchTerm}
					onChange={(event) => setSearchTerm(event.target.value)}
					disabled={saving}
				/>
			</div>

			{hasChanges ? (
				<div className="org-product-fields-sticky">
					<Space size={8} wrap className="org-product-fields-sticky-summary">
						<Text strong>Modifiche non salvate</Text>
						<Text type="secondary">Conferma per applicare gli aggiornamenti.</Text>
					</Space>
					<Space className="org-product-fields-actions" wrap>
						<Button
							type="primary"
							onClick={handleSave}
							loading={saving}
							disabled={!hasChanges || saving}
						>
							Salva modifiche
						</Button>
						<Button onClick={resetSelection} disabled={!hasChanges || saving}>
							Ripristina
						</Button>
					</Space>
				</div>
			) : null}

			{groupedDescriptors.length ? (
				<Collapse
					className="org-product-fields-collapse"
					bordered={false}
					activeKey={openGroups}
					onChange={handleGroupsChange}
					items={groupedDescriptors.map(group => ({
						key: group.key,
						label: (
							<div className="org-product-fields-group-title">
								<Space size={8} wrap>
									<Text strong>{group.label}</Text>
									<Tag color="blue" bordered={false}>{`${group.selectedCount}/${group.fields.length}`}</Tag>
								</Space>
							</div>
						),
						children: (
							<Space direction="vertical" size={12} className="org-product-fields-group">
								<div className="org-product-fields-group-toolbar">
									<Checkbox
										indeterminate={group.selectedCount > 0 && group.selectedCount < group.fields.length}
										checked={group.fields.length > 0 && group.selectedCount === group.fields.length}
										onChange={({target}) => handleGroupToggle(group.fields, target.checked)}
										disabled={saving}
										onClick={(event) => event.stopPropagation()}
									>
										{group.selectedCount === group.fields.length ? "Deseleziona tutto" : "Seleziona tutto"}
									</Checkbox>
									{group.extraCount ? <Tag color="gold" bordered={false}>{`${group.extraCount} fuori catalogo`}</Tag> : null}
								</div>

								<div className="org-product-fields-grid">
									{group.fields.map(field => (
										<div
											key={field.key}
											className={`org-product-fields-tile${field.checked ? " org-product-fields-tile--checked" : ""}`}
										>
											<div style={{display: "flex", alignItems: "flex-start", width: "100%"}}>
													<Checkbox
														className="org-product-fields-checkbox"
														checked={field.checked}
														onChange={({target}) => toggleSelection(field.key, target.checked)}
														disabled={saving}
														onClick={(event) => event.stopPropagation()}
														style={{flex: 1}}
													>
													<Space direction="vertical" size={4} style={{width: "100%"}}>
														<Text strong ellipsis={{tooltip: field.name}}>{field.name}</Text>
														<Space size={6} wrap>
															<Tag color="blue" bordered={false}>{field.typeLabel}</Tag>
															{field.isExtra ? <Tag color="gold" bordered={false}>Non più in catalogo</Tag> : null}
														</Space>
													</Space>
												</Checkbox>
												{field.checked && aiEnabledKeys.has(field.aiKeyNormalized) && (
													<div className="org-product-fields-tile-actions">
														<Tooltip title={aiDisabledKeys.has(field.aiKeyNormalized) ? "AI disattivata per questa cooperativa" : "AI attiva per questa cooperativa"}>
															<Switch
																className="org-ai-switch"
																size="small"
																checkedChildren="AI"
																unCheckedChildren="AI"
																checked={!aiDisabledKeys.has(field.aiKeyNormalized)}
																onChange={(checked) => handleToggleAiEnabled(field, checked)}
																onClick={(checked, event) => event?.stopPropagation?.()}
																disabled={saving}
															/>
														</Tooltip>
														{!aiDisabledKeys.has(field.aiKeyNormalized) && (
															<Tooltip title="Personalizza descrizione AI">
																<Badge className="org-ai-dot" dot={customAiFields.has(field.aiKeyNormalized)} offset={[-4, 4]}>
																	<Button
																		type="text"
																		size="small"
																		icon={<FileTextOutlined/>}
																		onClick={(e) => handleOpenAiModal(field, e)}
																		disabled={saving}
																	/>
																</Badge>
															</Tooltip>
														)}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							</Space>
						)
					}))}
				/>
			) : (
				<Empty
					description={hasSearch ? "Nessun campo corrisponde alla ricerca" : "Nessun input disponibile"}
					image={Empty.PRESENTED_IMAGE_SIMPLE}
				/>
			)}

			<AiDescriptionModal
				open={aiModalOpen}
				fieldKey={aiModalFieldKey}
				fieldLabel={aiModalFieldLabel}
				cooperativaId={cooperativaId}
				onClose={handleCloseAiModal}
				onSave={handleAiDescriptionSave}
			/>
		</div>
	);
}
